/// <amd-module name="TFS/Admin/Views/OrganizationOverview/Sources/OrganizationOverviewSources" />
import { getOrganizationClient } from "TFS/Admin/Common/RestClient/Organization";
import { Collection, Organization } from "TFS/Admin/Common/Contracts/Organization";
import { INewDomainUrlOrchestrationRestClient } from "TFS/Admin/Common/RestClient/NewDomainUrlOrchestration";
import { ServicingOrchestrationRequestStatus, ServicingOrchestrationStatus } from "TFS/Admin/Common/Contracts/NewDomainUrlMigration";
import { JsonPatchDocument, JsonPatchOperation, Operation } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { DataProviders } from "TFS/Admin/Common/Constants/DataProviderConstants";
import { IVssRestClientOptions, TeamFoundationHostType } from "VSS/Platform/Context";
import { WebSessionToken } from "VSS/Platform/Contracts/Authentication";
import { BearerTokenProvider } from "VSS/Platform/Authentication";
import { IVssPageContext } from "VSS/Platform/Context";

export class OrganizationOverviewSource {
    public renameCollection(newName: string, context: IVssComponentContext): Promise<Collection> {
        let createPatchDocument = (): JsonPatchDocument => {
            let operations: JsonPatchOperation[] = [];
            operations.push({
                from: "",
                op: Operation.Replace,
                path: DataProviders.NamePath,
                value: newName
            } as JsonPatchOperation);
            return operations as JsonPatchDocument;
        };

        let client = this.getCollectionClient(context);

        return client.updateCollection(createPatchDocument(), DataProviders.Me);
    }

    public deleteCollection(collectionId: string, pageContext: IVssPageContext, token?: WebSessionToken): Promise<boolean> {
        const client = getOrganizationClient(pageContext, {
            hostType: TeamFoundationHostType.Organization,
            authTokenProvider: token ? new BearerTokenProvider(token.token) : null
        } as IVssRestClientOptions);

        return client.deleteCollection(collectionId);
    }

    public forceUpdateCollectionOwner(
        forceUpdateReason: string,
        collectionId: string,
        context: IVssPageContext,
        newOwner: string
    ): Promise<Collection> {
        const client = getOrganizationClient(context);

        return client.forceUpdateCollectionOwner(forceUpdateReason, collectionId, newOwner);
    }

    public queueChangeDomain(devopsDomainUrls: boolean, context: IVssComponentContext): Promise<void> {
        let client = context.pageContext.getRestClient<INewDomainUrlOrchestrationRestClient>("INewDomainUrlOrchestrationRestClient");

        return client.queueMigration(devopsDomainUrls);
    }

    public async waitChangeDomainCompletion(context: IVssComponentContext): Promise<boolean> {
        let client = context.pageContext.getRestClient<INewDomainUrlOrchestrationRestClient>("INewDomainUrlOrchestrationRestClient");

        while (true) {
            let status: ServicingOrchestrationRequestStatus;

            try {
                status = await client.getStatus();

                if (status) {
                    if (status.status == ServicingOrchestrationStatus.Completed || status.status == ServicingOrchestrationStatus.Created) {
                        // success (we treat Created as Completed in case job history was not found)
                        return true;
                    } else if (status.status == ServicingOrchestrationStatus.Failed) {
                        // failure
                        return false;
                    }
                }
            } catch (e) {
                // report to console and keep polling
                console.error(e);
            }

            // still running, sleep and retry
            await this.sleep(1000);
        }
    }

    private async sleep(msec: number): Promise<void> {
        return new Promise<void>(resolve => setTimeout(resolve, msec));
    }

    public updateCollectionOwner(newOwnerId: string, pageContext: IVssPageContext): Promise<Collection> {
        let createPatchDocument = (): JsonPatchDocument => {
            let operations: JsonPatchOperation[] = [];
            operations.push({
                from: "",
                op: Operation.Replace,
                path: DataProviders.OwnerPath,
                value: newOwnerId
            } as JsonPatchOperation);
            return operations as JsonPatchDocument;
        };

        let client = getOrganizationClient(pageContext);

        return client.updateCollection(createPatchDocument(), DataProviders.Me);
    }

    public updateProperties(
        collectionId: string,
        context: IVssComponentContext,
        newDescription: string,
        newTimeZoneId: string,
        newPrivacyUrl: string
    ): Promise<boolean> {
        let createPatchDocument = (): JsonPatchDocument => {
            let operations: JsonPatchOperation[] = [];

            if (newDescription !== null) {
                operations.push({
                    from: "",
                    op: Operation.Replace,
                    path: DataProviders.DescriptionPath,
                    value: newDescription
                } as JsonPatchOperation);
            }

            if (newTimeZoneId) {
                operations.push({
                    from: "",
                    op: Operation.Replace,
                    path: DataProviders.TimeZonePath,
                    value: JSON.stringify(newTimeZoneId.trim())
                } as JsonPatchOperation);
            }

            if (newPrivacyUrl !== null) {
                operations.push({
                    from: "",
                    op: Operation.Replace,
                    path: DataProviders.PrivacyUrlPath,
                    value: newPrivacyUrl
                } as JsonPatchOperation);
            }

            return operations as JsonPatchDocument;
        };

        let client = this.getCollectionClient(context);

        return client.updateCollectionProperties(collectionId, createPatchDocument());
    }

    private getCollectionClient(context: IVssComponentContext) {
        return getOrganizationClient(context.pageContext);
    }
}
